import { type OrganizationAssetType } from "@/models/organization/dashboard-analytics/dashboard-analytics.model";
import {
	getOrganizationAssetTypes,
	selfOrganizationAssetTypes,
} from "@/services/organization-service";
import { useUserStore } from "@/stores/user-store";
import {
	ArrowLeftOutlined,
	CheckCircleOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	EditOutlined,
	KeyOutlined,
	ReloadOutlined,
	StopOutlined,
	SyncOutlined,
} from "@ant-design/icons";
import {
	Alert,
	Button,
	Col,
	Empty,
	Form,
	Layout,
	List,
	Modal,
	Popconfirm,
	Row,
	Select,
	Spin,
	Tag,
	Typography,
	message,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./SyncSettingsTab.scss";
import { DFPTable } from "@/components/tables/dfp-table";
import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";
import { getIntegrationConfig } from "@/services/integration-config-service";
import {
	getRunningJobs,
	getRunningPhotoSyncJobs,
	getSyncHistory,
	killJob,
	killPhotoSyncJob,
	updateIntegrationCreds,
} from "@/services/integration-service";
import { getOrganizationLocations } from "@/services/location-service";
import {
	type ServiceChannelAssetType,
	getServiceChannelAssetTypes,
} from "@/services/service-channel-service";
import { parseDateToLocalString } from "@/utils/transforms";
import axios from "axios";
import { AssetMappingModal } from "./AssetMappingModal";
import { CredentialsModal } from "./CredentialsModal";
import { SyncModal } from "./SyncModal";

const { Title, Text } = Typography;
const { Option } = Select;

interface MappingOption {
	id: string | number;
	name: string;
	category?: string;
	description?: string;
}

// Group mapping options by category
const groupMappingOptions = (options: MappingOption[]) => {
	const grouped: Record<string, MappingOption[]> = {};

	options.forEach((option) => {
		const category = option.category || "Uncategorized";
		if (!grouped[category]) {
			grouped[category] = [];
		}
		grouped[category].push(option);
	});

	return grouped;
};

interface SyncHistoryItem {
	id: number;
	organization_id: number;
	sync_time: string;
	sync_status: string;
	job_id: number;
}

interface RunningJob {
	id: number;
	organizationId: number;
	integrationId: number;
	jobStatus: string;
	startedAt: string;
	finishedAt?: string | null;
	details?: any;
	createdAt: string;
	updatedAt: string;
}

interface PhotoSyncJob {
	id: number;
	organizationId: number;
	integrationId: number;
	jobStatus: string;
	startedAt: string;
	finishedAt?: string | null;
	details?: any;
	createdAt: string;
	updatedAt: string;
}

const SyncSettingsTab = () => {
	// Navigation
	const navigate = useNavigate();
	const { integrationId } = useParams<{ integrationId: string }>();
	
	// State management
	const user = useUserStore((state) => state.user);
	const [assetTypes, setAssetTypes] = useState<OrganizationAssetType[]>([]);
	const [mappingOptions, setMappingOptions] = useState<MappingOption[]>([]);
	const [mappingOptions2, setMappingOptions2] = useState<MappingOption[]>([]);
	const [loading, setLoading] = useState(true);
	const [saveLoading, setSaveLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [mappings, setMappings] = useState<Record<number, string | number>>({});
	const [mappings2, setMappings2] = useState<Record<number, string | number>>(
		{},
	);
	const [scAssetTypes, setScAssetTypes] = useState<ServiceChannelAssetType[]>(
		[],
	);
	const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
	const [scLoading, setScLoading] = useState(false);
	const [isCredsModalOpen, setIsCredsModalOpen] = useState(false);
	const [credentials, setCredentials] = useState<{
		username: string;
		password: string;
	}>({
		username: "",
		password: "",
	});
	const [credsSaving, setCredsSaving] = useState(false);
	const [form] = Form.useForm();
	const [isSyncing, setIsSyncing] = useState(false);
	const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
	const [locations, setLocations] = useState<
		{ id: string; address: string; store_number?: string }[]
	>([]);
	const [loadingLocations, setLoadingLocations] = useState(false);
	const [syncHistory, setSyncHistory] = useState<SyncHistoryItem[]>([]);
	const [syncHistoryLoading, setSyncHistoryLoading] = useState(false);
	const [runningJobs, setRunningJobs] = useState<RunningJob[]>([]);
	const [runningJobsLoading, setRunningJobsLoading] = useState(false);
	const [killingJobId, setKillingJobId] = useState<number | null>(null);
	const [runningPhotoSyncJobs, setRunningPhotoSyncJobs] = useState<
		PhotoSyncJob[]
	>([]);
	const [runningPhotoSyncJobsLoading, setRunningPhotoSyncJobsLoading] =
		useState(false);
	const [killingPhotoSyncJobId, setKillingPhotoSyncJobId] = useState<
		number | null
	>(null);
	const [locationsModalOpen, setLocationsModalOpen] = useState(false);
	const [locationsModalLoading, setLocationsModalLoading] = useState(false);
	const [selectedJobLocations, setSelectedJobLocations] = useState<any[]>([]);
	const [selectedJobType, setSelectedJobType] = useState<
		"sync" | "photo" | null
	>(null);

	// Fetch running jobs
	const fetchRunningJobs = useCallback(async () => {
		try {
			setRunningJobsLoading(true);
			const response = await getRunningJobs();
			if (response.success) {
				setRunningJobs(response.data || []);
			}
		} catch (error) {
			console.error("Error fetching running jobs:", error);
			message.error("Failed to fetch running jobs");
		} finally {
			setRunningJobsLoading(false);
		}
	}, []);

	// Fetch running photo sync jobs
	const fetchRunningPhotoSyncJobs = useCallback(async () => {
		try {
			setRunningPhotoSyncJobsLoading(true);
			const response = await getRunningPhotoSyncJobs();
			if (response.success) {
				setRunningPhotoSyncJobs(response.data || []);
			}
		} catch (error) {
			console.error("Error fetching running photo sync jobs:", error);
			message.error("Failed to fetch running photo sync jobs");
		} finally {
			setRunningPhotoSyncJobsLoading(false);
		}
	}, []);

	// Fetch sync history
	const fetchSyncHistory = useCallback(async () => {
		try {
			setSyncHistoryLoading(true);
			const response = await getSyncHistory();
			if (response?.success && response?.data) {
				setSyncHistory(response.data);
			}
		} catch (error) {
			console.error("Error fetching sync history:", error);
			message.error("Failed to load sync history");
		} finally {
			setSyncHistoryLoading(false);
		}
	}, []);

	// Kill a job
	const handleKillJob = async (jobId: number) => {
		try {
			setKillingJobId(jobId);
			const response = await killJob(jobId);
			if (response.success) {
				message.success(`Job ${jobId} has been killed`);
				// Refresh running jobs and sync history
				await Promise.all([fetchRunningJobs(), fetchSyncHistory()]);
			}
		} catch (error) {
			console.error("Error killing job:", error);
			message.error("Failed to kill job");
		} finally {
			setKillingJobId(null);
		}
	};

	// Kill a photo sync job
	const handleKillPhotoSyncJob = async (jobId: number) => {
		try {
			setKillingPhotoSyncJobId(jobId);
			const response = await killPhotoSyncJob(jobId);
			if (response.success) {
				message.success(`Photo sync job ${jobId} has been killed`);
				// Refresh running photo sync jobs
				await fetchRunningPhotoSyncJobs();
			}
		} catch (error) {
			console.error("Error killing photo sync job:", error);
			message.error("Failed to kill photo sync job");
		} finally {
			setKillingPhotoSyncJobId(null);
		}
	};

	// Load asset types on component mount
	useEffect(() => {
		let isCancelled = false;

		const fetchData = async () => {
			try {
				setLoading(true);
				setError(null);

				// Fetch all asset types including deprecated for sync settings
				const [assetTypesData] = await Promise.all([
					getOrganizationAssetTypes(true),
				]);

				if (!isCancelled) {
					// Check if response has the expected structure from the API
					if (assetTypesData?.success && assetTypesData?.data?.assetTypes) {
						setAssetTypes(assetTypesData.data.assetTypes);
					} else if (Array.isArray(assetTypesData)) {
						// Fallback: handle if it returns an array directly
						setAssetTypes(assetTypesData);
					} else {
						// Last resort: try the old endpoint
						const fallbackData = await selfOrganizationAssetTypes(true);
						if (Array.isArray(fallbackData)) {
							setAssetTypes(fallbackData);
						} else {
							setError("Unable to retrieve asset types in the expected format");
						}
					}
					setLoading(false);
				}
			} catch (error) {
				if (!isCancelled) {
					setError("Failed to load data. Please try again later.");
					setLoading(false);
				}
			}
		};

		fetchData();

		// Call these functions but don't await them to avoid blocking the main data fetch
		fetchRunningJobs().catch(console.error);
		fetchRunningPhotoSyncJobs().catch(console.error);
		fetchSyncHistory().catch(console.error);

		return () => {
			isCancelled = true;
		};
	}, [
		user?.organization?.external_firebase_id,
		fetchRunningJobs,
		fetchRunningPhotoSyncJobs,
		fetchSyncHistory,
	]);

	// Update credentials from organization settings
	useEffect(() => {
		const settings = user?.organization?.config?.integration_settings as any;
		const sc = settings?.service_channel;
		if (sc) {
			setCredentials((prev) => ({
				username: sc.username || prev.username,
				password: sc.password || prev.password,
			}));
		}
	}, [user?.organization?.config?.integration_settings]);

	// Modal close handlers
	const handleCloseMappingModal = () => {
		setIsMappingModalOpen(false);
		setMappings({});
		setScAssetTypes([]);
	};

	const handleCloseCredsModal = () => {
		setIsCredsModalOpen(false);
		setCredentials({ username: "", password: "" });
	};

	const handleCloseSyncModal = () => {
		setIsSyncModalOpen(false);
		setLocations([]);
	};

	const handleCloseLocationsModal = () => {
		setLocationsModalOpen(false);
		setSelectedJobLocations([]);
		setSelectedJobType(null);
	};

	const fetchLocationsForJob = async (
		jobId: number,
		type: "sync" | "photo",
	) => {
		try {
			setLocationsModalLoading(true);
			setSelectedJobType(type);
			setLocationsModalOpen(true);

			const endpoint =
				type === "sync"
					? `/outside-source/integration/jobs/${jobId}/locations`
					: `/outside-source/integration/photo-jobs/${jobId}/locations`;

			const response = await axios.get(
				`${VITE_CORE_API_URL}${CORE_URL}${endpoint}`,
				{
					headers: {
						Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
					},
				},
			);

			if (response.data?.success) {
				setSelectedJobLocations(response.data.data || []);
			} else {
				message.error("Failed to load locations");
			}
		} catch (error) {
			console.error("Error fetching locations:", error);
			message.error("Failed to load locations for sync job");
		} finally {
			setLocationsModalLoading(false);
		}
	};

	// Event handlers
	const handleMapping = (assetTypeId: number, value: string | number) => {
		setMappings((prev) => ({ ...prev, [assetTypeId]: value }));
	};

	const saveMappings = async () => {
		try {
			setSaveLoading(true);
			// Only include mappings that have been explicitly set
			const assetTypeMap = assetTypes
				.filter((t) => mappings[t.id] !== undefined)
				.map((t) => {
					// Find the corresponding Service Channel asset type by either name or ID
					const scAssetType = scAssetTypes.find(
						(sc) => sc.name === mappings[t.id] || sc.id === mappings[t.id],
					);

					if (!scAssetType) {
						console.warn(
							`No matching Service Channel asset type found for ${t.name} with value ${mappings[t.id]}`,
						);
						return null;
					}

					return [
						t.name, // FacilityAI asset type as the key
						{
							type: scAssetType.name, // Service Channel asset type name
							trade: scAssetType.trade, // Service Channel trade
						},
					];
				})
				.filter(Boolean); // Remove any null entries

			if (assetTypeMap.length === 0) {
				message.warning(
					"No mappings have been set. Please set at least one mapping before saving.",
				);
				return;
			}

			const response = await axios.put(
				`${VITE_CORE_API_URL}${CORE_URL}/organization/self/asset-mappings`,
				{ assetTypeMap },
				{
					headers: {
						Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
					},
				},
			);

			if (response.data?.success) {
				message.success("Asset type mappings saved successfully");
				setIsMappingModalOpen(false);
			} else {
				throw new Error(response.data?.message || "Failed to save mappings");
			}
		} catch (error) {
			console.error("Error saving mappings:", error);
			if (axios.isAxiosError(error) && error.response?.data) {
				console.error("Server response:", error.response.data);
				message.error(
					error.response.data.message ||
						"Failed to save mappings. Please try again.",
				);
			} else {
				message.error(
					error instanceof Error
						? error.message
						: "Failed to save mappings. Please try again.",
				);
			}
		} finally {
			setSaveLoading(false);
		}
	};

	const openMappingModal = async () => {
		try {
			const config = await getIntegrationConfig();

			if (!config.username) {
				message.info("Please enter Service Channel credentials first.");
				try {
					const response = await axios.get(
						`${VITE_CORE_API_URL}${CORE_URL}/organization/self/integration-config`,
						{
							headers: {
								Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
							},
						},
					);

					if (response.data?.success && response.data?.data?.username) {
						setCredentials((prev) => ({
							...prev,
							username: response.data.data.username,
						}));
					}
				} catch (error) {
					console.error("Failed to fetch credentials:", error);
				}
				setIsCredsModalOpen(true);
				return;
			}

			setScLoading(true);
			setIsMappingModalOpen(true);

			// Get current config and Service Channel types
			const [types, currentMappings] = await Promise.all([
				getServiceChannelAssetTypes(),
				axios.get(
					`${VITE_CORE_API_URL}${CORE_URL}/organization/self/asset-mappings`,
					{
						headers: {
							Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
						},
					},
				),
			]);

			setScAssetTypes(types);

			// Set existing mappings if available
			if (
				currentMappings.data?.success &&
				currentMappings.data?.data?.assetTypeMap
			) {
				const existingMappings = currentMappings.data.data.assetTypeMap;
				const newMappings: Record<number, string | number> = {};

				existingMappings.forEach(
					([facilityAIType, config]: [
						string,
						{ type: string; trade: string },
					]) => {
						const assetType = assetTypes.find((t) => t.name === facilityAIType);
						const scType = types.find((t) => t.name === config.type);

						if (assetType && scType) {
							newMappings[assetType.id] = scType.name;
						}
					},
				);

				setMappings(newMappings);
			}
		} catch (e) {
			console.error("Error loading mappings:", e);
			message.error("Failed to load Service Channel types");
		} finally {
			setScLoading(false);
		}
	};

	const openCredsModal = async () => {
		try {
			const response = await axios.get(
				`${VITE_CORE_API_URL}${CORE_URL}/organization/self/integration-config`,
				{
					headers: {
						Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
					},
				},
			);

			if (response.data?.success && response.data?.data?.username) {
				setCredentials((prev) => ({
					...prev,
					username: response.data.data.username,
				}));
			}
			setIsCredsModalOpen(true);
		} catch (error) {
			console.error("Failed to fetch credentials:", error);
			message.error("Failed to load credentials");
		}
	};

	const saveCredentials = async () => {
		try {
			setCredsSaving(true);
			await updateIntegrationCreds(
				"service_channel",
				credentials.username,
				credentials.password,
				true,
				user?.organization?.id,
			);
			message.success("Credentials saved");
			setIsCredsModalOpen(false);
		} catch {
			message.error("Failed to save credentials");
		} finally {
			setCredsSaving(false);
		}
	};

	const handleSync = async (
		selectedLocations: string[],
		integrationId: string,
		serviceChannel: boolean,
		ecotrak: boolean,
		assetsQueued: number,
	) => {
		try {
			setIsSyncing(true);
			setIsSyncModalOpen(false);

			// Show success message with assets queued count
			message.success(`${assetsQueued} assets queued for sync successfully`);
		} catch (error) {
			message.error("Failed to sync assets. Please try again.");
		} finally {
			setIsSyncing(false);
		}
	};

	const fetchLocations = async () => {
		try {
			setLoadingLocations(true);
			const locationsData = await getOrganizationLocations();
			const formattedLocations = locationsData.map((loc: any) => ({
				id: loc.id.toString(),
				address: `${loc.address1}, ${loc.city}, ${loc.state} ${loc.zip}`,
				store_number: loc.store_number || undefined,
			}));
			setLocations(formattedLocations);
		} catch (error) {
			console.error("Error fetching locations:", error);
			message.error("Failed to fetch locations. Please try again.");
		} finally {
			setLoadingLocations(false);
		}
	};

	if (!user?.organization || loading) {
		return (
			<div className="loading-container">
				<Spin size="large" />
			</div>
		);
	}

	// Group mapping options by category
	const groupedOptions = groupMappingOptions(mappingOptions);
	const groupedOptions2 = groupMappingOptions(mappingOptions2);

	const syncHistoryColumns = [
		{
			title: "Job ID",
			dataIndex: "job_id",
			key: "job_id",
			width: 80,
			sorter: true,
		},
		{
			title: "Sync Time",
			dataIndex: "sync_time",
			key: "sync_time",
			sorter: true,
			render: (text: string) => parseDateToLocalString(text),
		},
		{
			title: "Status",
			dataIndex: "sync_status",
			key: "sync_status",
			sorter: true,
			render: (status: string) => (
				<span
					style={{
						color:
							status === "success"
								? "#52c41a"
								: status === "failed"
									? "#ff4d4f"
									: status === "in_progress"
										? "#1890ff"
										: "#000000",
					}}
				>
					{status.charAt(0).toUpperCase() + status.slice(1)}
				</span>
			),
		},
		{
			title: "Locations",
			key: "locations",
			width: 140,
			render: (_: any, record: any) => (
				<Button
					type="link"
					size="small"
					onClick={() =>
						fetchLocationsForJob(record.job_id || record.id, "sync")
					}
				>
					View Locations
				</Button>
			),
		},
	];

	const runningJobsColumns = [
		{
			title: "Job ID",
			dataIndex: "id",
			key: "id",
			width: 80,
		},
		{
			title: "Started At",
			dataIndex: "startedAt",
			key: "startedAt",
			render: (text: string) => parseDateToLocalString(text),
		},
		{
			title: "Status",
			dataIndex: "jobStatus",
			key: "jobStatus",
			render: (status: string) => (
				<span
					style={{
						color: status === "running" ? "#1890ff" : "#000000",
					}}
				>
					{status.charAt(0).toUpperCase() + status.slice(1)}
				</span>
			),
		},
		{
			title: "Locations",
			key: "locations",
			width: 140,
			render: (_: any, record: RunningJob) => (
				<Button
					type="link"
					size="small"
					onClick={() => fetchLocationsForJob(record.id, "sync")}
				>
					View Locations
				</Button>
			),
		},
		{
			title: "Actions",
			key: "actions",
			width: 120,
			render: (_: any, record: RunningJob) => (
				<Popconfirm
					title="Kill Job"
					description="Are you sure you want to kill this sync job?"
					onConfirm={() => handleKillJob(record.id)}
					okText="Yes"
					cancelText="No"
				>
					<Button
						type="primary"
						danger
						size="small"
						icon={<StopOutlined />}
						loading={killingJobId === record.id}
						disabled={killingJobId !== null}
					>
						Kill
					</Button>
				</Popconfirm>
			),
		},
	];

	const runningPhotoSyncJobsColumns = [
		{
			title: "Job ID",
			dataIndex: "id",
			key: "id",
			width: 80,
		},
		{
			title: "Started At",
			dataIndex: "startedAt",
			key: "startedAt",
			render: (text: string) => parseDateToLocalString(text),
		},
		{
			title: "Status",
			dataIndex: "jobStatus",
			key: "jobStatus",
			render: (status: string) => (
				<span
					style={{
						color: status === "running" ? "#1890ff" : "#000000",
					}}
				>
					{status.charAt(0).toUpperCase() + status.slice(1)}
				</span>
			),
		},
		{
			title: "Actions",
			key: "actions",
			width: 120,
			render: (_: any, record: PhotoSyncJob) => (
				<Popconfirm
					title="Kill Photo Sync Job"
					description="Are you sure you want to kill this photo sync job?"
					onConfirm={() => handleKillPhotoSyncJob(record.id)}
					okText="Yes"
					cancelText="No"
				>
					<Button
						type="primary"
						danger
						size="small"
						icon={<StopOutlined />}
						loading={killingPhotoSyncJobId === record.id}
						disabled={killingPhotoSyncJobId !== null}
					>
						Kill
					</Button>
				</Popconfirm>
			),
		},
	];

	// Get integration name from ID
	const getIntegrationName = (id: string | undefined) => {
		switch (id) {
			case "service_channel":
				return "Service Channel";
			default:
				return "Integration";
		}
	};

	return (
		<Layout className="dashboard-container">
			<Row gutter={[24, 24]} style={{ marginBottom: 16 }}>
				<Col flex="auto">
					<div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 8 }}>
						<Button
							type="text"
							icon={<ArrowLeftOutlined />}
							onClick={() => navigate("/admin/integrations")}
							style={{ padding: "4px 8px" }}
						>
							Back to Integrations
						</Button>
					</div>
					<Title level={3}>{getIntegrationName(integrationId)} Sync Settings</Title>
					<Text type="secondary">
						Manage sync jobs and view sync history for this organization.
					</Text>
				</Col>
				<Col>
					<Button
						type="default"
						size="large"
						style={{ marginRight: 8, minWidth: "140px" }}
						onClick={() => {
							fetchRunningJobs();
							fetchRunningPhotoSyncJobs();
							fetchSyncHistory();
						}}
						icon={<ReloadOutlined />}
					>
						Refresh
					</Button>
					<Button
						type="primary"
						size="large"
						style={{ marginRight: 8, minWidth: "140px" }}
						onClick={openMappingModal}
						icon={<EditOutlined />}
					>
						Edit Mappings
					</Button>
					<Button
						type="primary"
						size="large"
						style={{ marginRight: 8, minWidth: "140px" }}
						onClick={openCredsModal}
						icon={<KeyOutlined />}
					>
						Credentials
					</Button>
					<Button
						type="primary"
						icon={<SyncOutlined />}
						loading={isSyncing}
						onClick={() => {
							fetchLocations();
							setIsSyncModalOpen(true);
						}}
						size="large"
						style={{ minWidth: "140px" }}
					>
						Sync Assets
					</Button>
				</Col>
			</Row>

			{error && (
				<Alert
					message="Error"
					description={error}
					type="error"
					showIcon
					style={{ marginBottom: 24 }}
				/>
			)}

			{!error && (!assetTypes || assetTypes.length === 0) && (
				<Empty
					description="No asset types available"
					style={{ margin: "48px 0" }}
				/>
			)}

			{/* Running Jobs Section */}
			<Row style={{ marginTop: 24 }}>
				<Col span={24}>
					<Title level={4}>Currently Running Jobs</Title>
					<Text type="secondary" style={{ marginBottom: 16, display: "block" }}>
						Jobs that are currently running. You can kill stuck jobs here.
					</Text>
					{runningJobs.length === 0 && !runningJobsLoading ? (
						<Alert
							message="No running jobs"
							description="There are currently no sync jobs running."
							type="info"
							showIcon
							style={{ marginBottom: 24 }}
						/>
					) : (
						<DFPTable
							columns={runningJobsColumns as any}
							dataSource={runningJobs}
							loading={runningJobsLoading}
							rowKey="id"
							pagination={false}
							size="small"
							style={{ marginBottom: 24 }}
						/>
					)}
				</Col>
			</Row>

			{/* Running Photo Sync Jobs Section */}
			<Row style={{ marginTop: 24 }}>
				<Col span={24}>
					<Title level={4}>Currently Running Photo Sync Jobs</Title>
					<Text type="secondary" style={{ marginBottom: 16, display: "block" }}>
						Photo sync jobs that are currently running. You can kill stuck photo
						sync jobs here.
					</Text>
					{runningPhotoSyncJobs.length === 0 && !runningPhotoSyncJobsLoading ? (
						<Alert
							message="No running photo sync jobs"
							description="There are currently no photo sync jobs running."
							type="info"
							showIcon
							style={{ marginBottom: 24 }}
						/>
					) : (
						<DFPTable
							columns={runningPhotoSyncJobsColumns as any}
							dataSource={runningPhotoSyncJobs}
							loading={runningPhotoSyncJobsLoading}
							rowKey="id"
							pagination={false}
							size="small"
							style={{ marginBottom: 24 }}
						/>
					)}
				</Col>
			</Row>

			{/* Sync History Table */}
			<Row style={{ marginTop: 32 }}>
				<Col span={24}>
					<Title level={4}>Sync History</Title>
					<Text type="secondary" style={{ marginBottom: 16, display: "block" }}>
						A history of previous sync jobs ran for this organization.
					</Text>
					<DFPTable
						columns={syncHistoryColumns}
						dataSource={syncHistory}
						loading={syncHistoryLoading}
						rowKey="id"
						pagination={{
							pageSize: 10,
							showSizeChanger: true,
							showTotal: (total) => `Total ${total} items`,
						}}
					/>
				</Col>
			</Row>

			{/* Sync Modal */}
			<SyncModal
				isOpen={isSyncModalOpen}
				toggle={handleCloseSyncModal}
				organizationName={user?.organization?.external_firebase_id || ""}
				locations={locations}
				onSync={handleSync}
				loading={loadingLocations}
			/>
			<AssetMappingModal
				open={isMappingModalOpen}
				onClose={handleCloseMappingModal}
				assetTypes={assetTypes}
				scAssetTypes={scAssetTypes}
				mappings={mappings}
				onChange={handleMapping}
				onSave={saveMappings}
				saving={saveLoading}
				loading={scLoading}
			/>
			<CredentialsModal
				open={isCredsModalOpen}
				onClose={handleCloseCredsModal}
				credentials={credentials}
				setCredentials={setCredentials}
				onSave={saveCredentials}
				saving={credsSaving}
			/>

			{/* Locations Modal */}
			<Modal
				title={`Syncing Locations - ${selectedJobType === "sync" ? "Integration Sync" : "Photo Sync"}`}
				open={locationsModalOpen}
				onCancel={handleCloseLocationsModal}
				footer={[
					<Button key="close" onClick={handleCloseLocationsModal}>
						Close
					</Button>,
				]}
				width={800}
			>
				<Spin spinning={locationsModalLoading}>
					{selectedJobLocations.length === 0 && !locationsModalLoading ? (
						<Empty description="No locations found for this sync job" />
					) : (
						<List
							dataSource={selectedJobLocations}
							renderItem={(location: any) => {
								const statusIcon =
									location.syncStatus === "success" ? (
										<CheckCircleOutlined />
									) : location.syncStatus === "failed" ? (
										<CloseCircleOutlined />
									) : location.syncStatus === "running" ? (
										<SyncOutlined spin />
									) : (
										<ClockCircleOutlined />
									);

								const statusColor =
									location.syncStatus === "success"
										? "green"
										: location.syncStatus === "failed"
											? "red"
											: location.syncStatus === "running"
												? "blue"
												: "default";

								return (
									<List.Item
										key={location.locationId}
										extra={
											<div
												style={{
													display: "flex",
													gap: 8,
													alignItems: "center",
												}}
											>
												<Tag icon={statusIcon} color={statusColor}>
													{location.syncStatus}
												</Tag>
												{selectedJobType === "photo" && (
													<>
														<Tag color="green">
															↑ {location.photosUploaded || 0}
														</Tag>
														<Tag color="orange">
															⊘ {location.photosSkipped || 0}
														</Tag>
														<Tag color="red">
															✗ {location.photosFailed || 0}
														</Tag>
													</>
												)}
											</div>
										}
									>
										<List.Item.Meta
											title={
												<span>
													{location.locationName || location.address}
													{location.storeNumber && (
														<Text type="secondary" style={{ marginLeft: 8 }}>
															(#{location.storeNumber})
														</Text>
													)}
												</span>
											}
											description={
												<>
													<div>{location.address}</div>
													{location.lastSyncedAt && (
														<Text type="secondary" style={{ fontSize: 12 }}>
															Last synced:{" "}
															{new Date(location.lastSyncedAt).toLocaleString()}
														</Text>
													)}
												</>
											}
										/>
									</List.Item>
								);
							}}
						/>
					)}
				</Spin>
			</Modal>
		</Layout>
	);
};

export { SyncSettingsTab };
